import React from 'react';
import { captureException } from '@/utils/sentry';

interface OrderEntryErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

interface OrderEntryErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

/**
 * ErrorBoundary for Order Entry that handles errors from OrderEntryOrderGuide
 * and coordinates with restaurant list errors.
 *
 * Uses useReducer to trigger re-renders when retry is pressed.
 */
export class OrderEntryErrorBoundary extends React.Component<
  OrderEntryErrorBoundaryProps,
  OrderEntryErrorBoundaryState
> {
  constructor(props: OrderEntryErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): OrderEntryErrorBoundaryState {
    return {
      error,
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'OrderEntryErrorBoundary',
      },
    });
  }

  handleRetryClick = () => {
    // Reset error state to allow component to re-render
    this.setState({
      error: null,
      hasError: false,
    });
  };

  render() {
    // Show error state if there's an error OR if restaurant list is empty
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
